/**
 * Member Activation — the shared vocabulary catalog.
 *
 * One home for everything the platform's activation report and the webapp's
 * dashboard must agree on: the three health pools, the four milestones and
 * their day windows, the per-milestone states, the health factors,
 * and the activation stages. The platform computes with these values; the
 * webapp renders them — neither side restates a label, threshold, or enum.
 *
 * Health is the ENG-4031 compound matrix over three factors (milestones,
 * delight, experience): any factor at risk → at risk; two or more factors
 * needing attention → at risk (compound); exactly one → needs attention.
 *
 * Product retunes a milestone deadline by editing ONE row in
 * {@link ACTIVATION_MILESTONES} (e.g. the proposed 30/45-day tightening from
 * the 2026-07 review thread); the scoring logic and every UI surface follow.
 */

// ── Health pools ────────────────────────────────────────────────────────────

export const MEMBER_HEALTH_VALUES = [
  "at_risk",
  "needs_attention",
  "on_track",
] as const;

export type MemberHealth = (typeof MEMBER_HEALTH_VALUES)[number];

/** Severity-first — drives the pool tile order AND the default roster sort. */
export const MEMBER_HEALTH_ORDER: readonly MemberHealth[] =
  MEMBER_HEALTH_VALUES;

export const MEMBER_HEALTH_META: Record<
  MemberHealth,
  { label: string; definition: string }
> = {
  at_risk: {
    label: "At Risk",
    definition:
      "Something needs intervention: a factor is at risk (a milestone past its deadline, an at-risk order, or only thumbs-down ratings) — or two or more factors need attention at once.",
  },
  needs_attention: {
    label: "Needs Attention",
    definition:
      "Falling behind: exactly one factor needs attention — a milestone past its attention day, mixed ratings, or an order needing attention.",
  },
  on_track: {
    label: "On Track",
    definition:
      "Progressing normally: every factor that applies — milestones, ratings, related orders — is on track.",
  },
};

export function isMemberHealth(
  value: string | null | undefined,
): value is MemberHealth {
  return (MEMBER_HEALTH_VALUES as readonly string[]).includes(value ?? "");
}

/** Worst-of roll-up level per health (0 = at_risk … 2 = on_track). */
export const MEMBER_HEALTH_LEVEL: Record<MemberHealth, 0 | 1 | 2> = {
  at_risk: 0,
  needs_attention: 1,
  on_track: 2,
};

// ── Milestones ──────────────────────────────────────────────────────────────

export interface ActivationMilestone {
  key: "onboarding" | "homeEvaluation" | "firstFix" | "firstService";
  label: string;
  /** Roster chip text (the design's Onb / Eval / Fix / Svc). */
  shortLabel: string;
  /** Open past this day-since-join → needs_attention. */
  attnDay: number;
  /** Open past this day-since-join → at_risk. The "Day N" shown on cards. */
  dueDay: number;
  /** Applicable even when the phase has no orders: the milestone clocks from
   *  day 0 and never passes as `not_applicable` — a member cannot activate
   *  without it. Phase existence governs the milestones where this is false. */
  alwaysRequired: boolean;
  definition: string;
}

/**
 * The four activation milestones, in journey order. NOT sequential — a member
 * can complete them in any order, so each is scored independently.
 */
export const ACTIVATION_MILESTONES: readonly ActivationMilestone[] = [
  {
    key: "onboarding",
    label: "Onboarding",
    shortLabel: "Onb",
    attnDay: 7,
    dueDay: 15,
    // Every membership starts with virtual onboarding — the phase is
    // universal, never purchasable-optional, so a missing session order means
    // provisioning failed, not that the milestone doesn't apply. Zero live
    // rows change (191/191 members have the phase); this closes the
    // silent-provisioning-failure hole (QA 2026-08-14).
    alwaysRequired: true,
    definition: "The virtual onboarding session held (visit-based).",
  },
  {
    key: "homeEvaluation",
    label: "Home evaluation",
    shortLabel: "Eval",
    attnDay: 15,
    dueDay: 30,
    // Purchasable/waivable at checkout — the plan key cannot tell who bought
    // one. Applicability reads order existence PLUS the recorded checkout
    // purchase (Onboarding.currentState.homeEvaluationOrderIncluded, joined in
    // the report query): a paid evaluation clocks even while its only order is
    // still a draft (QA 2026-08-14 — three paid HEs sat invisible as N/A).
    alwaysRequired: false,
    definition: "The in-home evaluation visit completed.",
  },
  {
    key: "firstFix",
    label: "First fix visit",
    shortLabel: "Fix",
    attnDay: 30,
    dueDay: 60,
    alwaysRequired: false,
    definition: "The first quarterly Birdwatch Fix completed.",
  },
  {
    key: "firstService",
    label: "First service",
    shortLabel: "Svc",
    attnDay: 30,
    dueDay: 60,
    // Recurring service is the program's purpose: a zero-service member must
    // clock this milestone, not silently pass and activate (QA 2026-08-14 —
    // 9 members had auto-activated without any service order).
    alwaysRequired: true,
    definition:
      "The first standalone service order (outside onboarding and fixes) completed.",
  },
];

export type ActivationMilestoneKey = ActivationMilestone["key"];

const ACTIVATION_MILESTONE_KEYS: readonly ActivationMilestoneKey[] =
  ACTIVATION_MILESTONES.map((milestone) => milestone.key);

export function getActivationMilestone(
  key: string,
): ActivationMilestone | undefined {
  return ACTIVATION_MILESTONES.find((milestone) => milestone.key === key);
}

export function isActivationMilestoneKey(
  value: string | null | undefined,
): value is ActivationMilestoneKey {
  return (ACTIVATION_MILESTONE_KEYS as readonly string[]).includes(value ?? "");
}

// ── Per-milestone state ─────────────────────────────────────────────────────

/**
 * The states a milestone can be in, healthiest first. `complete` is
 * healthy forever, whenever it landed; `scheduled` means booked on or before
 * the due day (also healthy); the open states are decided purely by days
 * since join vs the milestone's window. `not_applicable` means the phase has
 * NO orders at all — it does not exist for this member and PASSES (never
 * contributes to health, never blocks activation); evaluation starts when
 * the phase's first order is created. Milestones flagged `alwaysRequired`
 * (onboarding, first service) never take this state.
 */
export const MILESTONE_STATE_VALUES = [
  "complete",
  "scheduled",
  "on_track",
  "needs_attention",
  "at_risk",
  "not_applicable",
] as const;

export type MilestoneState = (typeof MILESTONE_STATE_VALUES)[number];

export function isMilestoneState(
  value: string | null | undefined,
): value is MilestoneState {
  return (MILESTONE_STATE_VALUES as readonly string[]).includes(value ?? "");
}

export const MILESTONE_STATE_META: Record<MilestoneState, { label: string }> = {
  complete: { label: "Complete" },
  scheduled: { label: "Scheduled in window" },
  on_track: { label: "Open, on track" },
  needs_attention: { label: "Needs attention" },
  at_risk: { label: "At risk" },
  not_applicable: { label: "Not applicable" },
};

/** Health contribution of a milestone state to the member's roll-up.
 *  `not_applicable` maps to the healthy level as a safe neutral, but the
 *  roll-up skips N/A milestones entirely rather than counting them. */
export const MILESTONE_STATE_LEVEL: Record<MilestoneState, 0 | 1 | 2> = {
  complete: 2,
  scheduled: 2,
  on_track: 2,
  needs_attention: 1,
  at_risk: 0,
  not_applicable: 2,
};

// ── Health drivers ──────────────────────────────────────────────────────────

/** The factor groups that can drag a member's health down. Experience
 * (related-order health from Order Overview) was removed 2026-08-12 and
 * reinstated 2026-08-14 by ENG-4031's compound risk matrix, which names all
 * three factors explicitly. */
export const ACTIVATION_DRIVER_VALUES = [
  "milestones",
  "experience",
  "delight",
] as const;

export type ActivationDriver = (typeof ACTIVATION_DRIVER_VALUES)[number];

export function isActivationDriver(
  value: string | null | undefined,
): value is ActivationDriver {
  return (ACTIVATION_DRIVER_VALUES as readonly string[]).includes(value ?? "");
}

export const ACTIVATION_DRIVER_META: Record<
  ActivationDriver,
  { label: string; definition: string }
> = {
  milestones: {
    label: "Milestones",
    definition: "Progress against the activation milestone windows.",
  },
  experience: {
    label: "Experience",
    definition:
      "Order Overview's verdicts on the member's related orders — delivered orders count as on track; open orders (a held visit included) read their latest verdict.",
  },
  delight: {
    label: "Delight",
    definition: "The mix of thumbs-up and thumbs-down ratings the member left.",
  },
};

// ── Activation stage ────────────────────────────────────────────────────────

/**
 * Report scope. A member is ACTIVATED once every applicable milestone is
 * complete — or when a coordinator manually marks them activated
 * (MembershipActivation). The report's default scope is everyone still
 * activating — regardless of how long ago they joined (the boundary is
 * activation, not time).
 */
export const ACTIVATION_STAGE_VALUES = [
  "activating",
  "activated",
  "all",
] as const;

export type ActivationStage = (typeof ACTIVATION_STAGE_VALUES)[number];

export const DEFAULT_ACTIVATION_STAGE: ActivationStage = "activating";

export const ACTIVATION_STAGE_META: Record<ActivationStage, { label: string }> =
  {
    activating: { label: "Still activating" },
    activated: { label: "Activated" },
    all: { label: "Everyone" },
  };
